/**
 * DPIA measure-link consumption + pure analysis helpers.
 *
 * Historical note: this module used to issue N+M live HTTP fetches
 * (getControl / getTask per pointer) on every Report-tab mount.
 * Post-VER-284 BE-D the assessment GET response carries a
 * ``links_hydrated`` sibling block that resolves every pointer
 * server-side in one connection. The FE now consumes that block —
 * this file is pure data transformation with zero I/O.
 *
 * Callers: `<app-dpia-report>`, `<app-dpia-full-profile>` (chip render),
 * `edit-assessment.ts` route (unresolved-measure counts for the
 * coverage-action strip + walk-loop kickoff).
 */

import type { DpiaMeasure, DpiaRiskProfileData } from '../../../../models/assessments/dpia.model';
import { measureHasControl, measureHasTask } from '../../../../models/assessments/dpia.model';
import type { LinksHydrated } from '../../../../models/httpApi.model';

/** Display-ready control summary — the shape `<app-dpia-measures>` reads. */
export interface ControlSummary {
  control_id: string;
  control_reference: string | null;
  /** Framework (ISO) reference; null for a custom control or a snapshot. */
  template_reference: string | null;
  name: string;
  implementation_status: string;
  archived: boolean;
  /** Provenance — snapshot on APPROVED DPIAs, live-fetched on DRAFT.
   *  FE renderers may use this to pick a subtle "frozen" affordance. */
  source: 'snapshot' | 'hydrated';
}

/** Display-ready task summary. */
export interface TaskSummary {
  task_id: string;
  name: string;
  status: string;
  /** Null on a snapshot (frozen rows carry name + status only). */
  task_type: string | null;
  due_date: string | null;
  completed_at: string | null;
  source: 'snapshot' | 'hydrated';
}

/** Result of a single traversal over `dpia.measures[]` — all counts +
 *  id lists a caller might want. Callers pull named fields. */
interface DpiaMeasureAnalysis {
  unresolvedControlIds: string[];
  unresolvedTaskIds: string[];
  unresolvedControlCount: number;
  unresolvedTaskCount: number;
  /** Not fully implemented, with neither a control nor a task: what the
   *  Tasks banner counts and bulk create acts on. */
  needsTaskIds: string[];
}

/**
 * Build the two lookup maps used by the measures render. Prefers the
 * measure's frozen `linked_*_snapshot` when present (APPROVED-DPIA
 * audit exhibit); otherwise pulls from the BE-hydrated block on the
 * response envelope. Missing keys (fetch failed / target archived)
 * surface as absent map entries — the FE renders a warning chip.
 */
export function buildLinkLookups(
  dpia: DpiaRiskProfileData | null | undefined,
  hydrated: LinksHydrated | null | undefined
): { controls: Map<string, ControlSummary>; tasks: Map<string, TaskSummary> } {
  const controls = new Map<string, ControlSummary>();
  const tasks = new Map<string, TaskSummary>();

  // 1. Prefer snapshot on link rows that carry one. Post-VER-284 M:N
  //    shape — each measure has arrays of `controls[]` / `tasks[]`
  //    link rows, and the frozen snapshot lives on the LINK ROW (not
  //    the measure). Iterate every link.
  for (const m of dpia?.measures ?? []) {
    for (const link of m.controls ?? []) {
      if (!link.snapshot) continue;
      controls.set(link.control_id, {
        control_id: link.control_id,
        control_reference: link.snapshot.control_reference ?? null,
        template_reference: null,
        name: link.snapshot.name,
        implementation_status: link.snapshot.implementation_status,
        archived: false,
        source: 'snapshot',
      });
    }
    for (const link of m.tasks ?? []) {
      if (!link.snapshot) continue;
      tasks.set(link.task_id, {
        task_id: link.task_id,
        name: link.snapshot.name,
        status: link.snapshot.status,
        task_type: null,
        due_date: null,
        completed_at: null,
        source: 'snapshot',
      });
    }
  }

  // 2. Fill from the BE-hydrated block for any pointers without a
  //    snapshot. `hydrated` entries never overwrite snapshot entries
  //    (BE already skipped hydration for snapshotted pointers per
  //    `dpia_links_hydration.build_links_hydrated`).
  for (const c of hydrated?.controls ?? []) {
    if (controls.has(c.control_id)) continue;
    controls.set(c.control_id, {
      control_id: c.control_id,
      control_reference: c.control_reference ?? null,
      template_reference: c.template_reference ?? null,
      name: c.name,
      implementation_status: c.implementation_status,
      archived: c.archived,
      source: 'hydrated',
    });
  }
  for (const t of hydrated?.tasks ?? []) {
    if (tasks.has(t.task_id)) continue;
    tasks.set(t.task_id, {
      task_id: t.task_id,
      name: t.name,
      status: t.status,
      task_type: t.task_type ?? null,
      due_date: t.due_date ?? null,
      completed_at: t.completed_at ?? null,
      source: 'hydrated',
    });
  }

  return { controls, tasks };
}

/** Single O(N) traversal producing every measure-derived count / id
 *  list the FE consumes. Replaces the 5 near-duplicate traversals of
 *  the earlier draft. */
export function analyseDpiaMeasures(
  dpia: DpiaRiskProfileData | null | undefined
): DpiaMeasureAnalysis {
  const unresolvedControlIds: string[] = [];
  const unresolvedTaskIds: string[] = [];
  const needsTaskIds: string[] = [];
  for (const m of dpia?.measures ?? ([] as DpiaMeasure[])) {
    const hasControl = measureHasControl(m);
    const hasTask = measureHasTask(m);
    if (!hasControl) unresolvedControlIds.push(m.measure_id);
    if (!hasTask) unresolvedTaskIds.push(m.measure_id);
    if (m.coverage !== 'FULL' && !hasControl && !hasTask) needsTaskIds.push(m.measure_id);
  }
  return {
    unresolvedControlIds,
    unresolvedTaskIds,
    unresolvedControlCount: unresolvedControlIds.length,
    unresolvedTaskCount: unresolvedTaskIds.length,
    needsTaskIds,
  };
}
